import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  LogOut,
  Users,
  MousePointerClick,
  Percent,
  Globe,
  RefreshCcw,
  Smartphone,
  Link as LinkIcon,
  Plus,
  Trash2,
  Copy,
  Check,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const SERIF = { fontFamily: "'Nacelle', -apple-system, sans-serif", fontWeight: 600 };
const SANS = { fontFamily: "'Nacelle', -apple-system, sans-serif", fontWeight: 400 };

const COLORS = ['#FF6E00', '#FF8533', '#FFA66B', '#FFD1A6', '#7C7C7C'];

const RANGES = [
  { key: '24h', label: '24H' },
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: '90d', label: '90D' },
  { key: 'all', label: 'ALL' },
  { key: 'custom', label: 'CUSTOM' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const COUNTRY_META = {
  IT: { name: 'Italy', flag: '\ud83c\uddee\ud83c\uddf9' },
  GR: { name: 'Greece', flag: '\ud83c\uddec\ud83c\uddf7' },
  CY: { name: 'Cyprus', flag: '\ud83c\udde8\ud83c\uddfe' },
  PH: { name: 'Philippines', flag: '\ud83c\uddf5\ud83c\udded' },
  SG: { name: 'Singapore', flag: '\ud83c\uddf8\ud83c\uddec' },
  BN: { name: 'Brunei', flag: '\ud83c\udde7\ud83c\uddf3' },
};

const APP_ORIGIN =
  typeof window !== 'undefined' && window.location
    ? window.location.origin
    : 'https://v2.mbealert.com';

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-white/[0.025] border border-white/10 rounded-2xl p-5">
    <div className="flex items-center gap-2 text-white/50 text-[11px] tracking-[0.15em] uppercase">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
    <div className="mt-2 text-white text-3xl tracking-[-1px] leading-none" style={SERIF}>
      {value}
    </div>
    {sub && <div className="mt-2 text-white/40 text-[11.5px]">{sub}</div>}
  </div>
);

const SectionCard = ({ title, children, right }) => (
  <div className="bg-white/[0.025] border border-white/10 rounded-2xl p-5 sm:p-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-white text-lg tracking-[-0.3px]" style={SERIF}>
        {title}
      </h3>
      {right}
    </div>
    {children}
  </div>
);

const formatNum = (n) =>
  n == null ? '\u2014' : Number(n).toLocaleString();
const formatPct = (v) =>
  v == null ? '\u2014' : `${(Number(v) * 100).toFixed(1)}%`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState('7d');
  const [customStart, setCustomStart] = useState(daysAgoISO(30));
  const [customEnd, setCustomEnd] = useState(todayISO());
  const [summary, setSummary] = useState(null);
  const [byCountry, setByCountry] = useState([]);
  const [timeseries, setTimeseries] = useState([]);
  const [sources, setSources] = useState([]);
  const [devices, setDevices] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [utm, setUtm] = useState([]);
  const [recent, setRecent] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countryForm, setCountryForm] = useState({ code: '', name: '', flag: '', telegram_link: '', enabled: true });
  const [savingCountry, setSavingCountry] = useState(false);
  const [countryMsg, setCountryMsg] = useState(null);
  const [utmForm, setUtmForm] = useState({ country: 'IT', campaign: '', source: 'facebook', medium: 'paid_social', content: '' });
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem('mbe_admin_token') : null;
  const exp = typeof window !== 'undefined' ? Number(localStorage.getItem('mbe_admin_exp') || 0) : 0;
  const isExpired = !token || (exp && Date.now() > exp);

  useEffect(() => {
    if (isExpired) {
      navigate('/admin/login');
    }
  }, [isExpired, navigate]);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    if (isExpired) return;
    setRefreshing(true);
    try {
      const isCustom = range === 'custom';
      const params = new URLSearchParams();
      if (isCustom) {
        if (customStart) params.set('start', customStart);
        if (customEnd) params.set('end', customEnd);
      } else {
        params.set('range', range);
      }
      const q = `?${params.toString()}`;
      const endpoints = [
        `/api/admin/summary${q}`,
        `/api/admin/by-country${q}`,
        `/api/admin/timeseries${q}`,
        `/api/admin/sources${q}`,
        `/api/admin/devices${q}`,
        `/api/admin/referrers${q}`,
        `/api/admin/utm${q}`,
        `/api/admin/recent?limit=50`,
      ];
      const results = await Promise.all(
        endpoints.map((p) =>
          fetch(`${BACKEND_URL}${p}`, { headers: authHeaders }).then((r) => {
            if (r.status === 401) throw new Error('unauthorized');
            return r.json();
          })
        )
      );
      setSummary(results[0]);
      setByCountry(results[1] || []);
      setTimeseries(results[2] || []);
      setSources(results[3] || []);
      setDevices(results[4] || []);
      setReferrers(results[5] || []);
      setUtm(results[6] || []);
      setRecent(results[7] || []);
    } catch (err) {
      if (String(err.message).includes('unauthorized')) {
        localStorage.removeItem('mbe_admin_token');
        localStorage.removeItem('mbe_admin_exp');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [range, customStart, customEnd, authHeaders, isExpired, navigate]);

  const fetchCountries = useCallback(async () => {
    if (isExpired) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/countries`, { headers: authHeaders });
      if (res.status === 401) throw new Error('unauthorized');
      const data = await res.json();
      setCountries(Array.isArray(data) ? data : []);
    } catch (e) { /* noop */ }
  }, [authHeaders, isExpired]);

  const saveCountry = async (e) => {
    e.preventDefault();
    setSavingCountry(true);
    setCountryMsg(null);
    try {
      const body = {
        code: (countryForm.code || '').trim().toUpperCase(),
        name: countryForm.name.trim(),
        flag: countryForm.flag.trim(),
        telegram_link: countryForm.telegram_link.trim(),
        enabled: !!countryForm.enabled,
      };
      const res = await fetch(`${BACKEND_URL}/api/admin/countries`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Save failed');
      }
      setCountryMsg({ ok: true, text: `Saved ${body.code}` });
      setCountryForm({ code: '', name: '', flag: '', telegram_link: '', enabled: true });
      await fetchCountries();
    } catch (err) {
      setCountryMsg({ ok: false, text: err.message });
    } finally {
      setSavingCountry(false);
    }
  };

  const deleteCountry = async (code) => {
    if (!window.confirm(`Delete country ${code}?`)) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/countries/${code}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      await fetchCountries();
    } catch (e) { /* noop */ }
  };

  const editCountry = (c) => setCountryForm({
    code: c.code, name: c.name || '', flag: c.flag || '',
    telegram_link: c.telegram_link || '', enabled: c.enabled !== false,
  });

  const utmUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (utmForm.country) params.set('c', utmForm.country);
    if (utmForm.source) params.set('utm_source', utmForm.source);
    if (utmForm.medium) params.set('utm_medium', utmForm.medium);
    if (utmForm.campaign) params.set('utm_campaign', utmForm.campaign);
    if (utmForm.content) params.set('utm_content', utmForm.content);
    return `${APP_ORIGIN}/?${params.toString()}`;
  }, [utmForm]);

  const copyUtmUrl = async () => {
    try {
      await navigator.clipboard.writeText(utmUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { fetchCountries(); }, [fetchCountries]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Auto-refresh every 30 sec
  useEffect(() => {
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
  }, [fetchAll]);

  const logout = () => {
    localStorage.removeItem('mbe_admin_token');
    localStorage.removeItem('mbe_admin_exp');
    navigate('/admin/login');
  };

  const totalSources = sources.reduce((a, b) => a + b.count, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" style={SANS}>
        <div className="text-white/60">Loading dashboard\u2026</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" style={SANS}>
      {/* Header */}
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-white/8">
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between gap-3">
          <div>
            <div className="text-white text-lg tracking-[-0.3px]" style={SERIF}>
              MBE Alert Analytics
            </div>
            <div className="text-white/40 text-[11.5px]">Real-time landing page analytics</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={fetchAll}
              className="inline-flex items-center gap-1.5 text-white/80 hover:text-white bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 px-3 py-2 rounded-full text-[12.5px] transition-colors"
              disabled={refreshing}
            >
              <RefreshCcw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-white/80 hover:text-white bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 px-3 py-2 rounded-full text-[12.5px] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 py-6 space-y-5">
        {/* Range selector */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white/50 text-[11px] tracking-[0.15em] uppercase">Range</span>
          <div className="inline-flex bg-white/[0.03] border border-white/10 rounded-full p-1">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRange(r.key)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] transition-colors ${
                  range === r.key
                    ? 'bg-[#FF6E00] text-white'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          {range === 'custom' && (
            <div className="flex items-center gap-2 flex-wrap ml-1">
              <input
                type="date"
                value={customStart}
                max={customEnd || undefined}
                onChange={(e) => setCustomStart(e.target.value)}
                className="bg-white/[0.05] border border-white/10 rounded-full px-3 py-1.5 text-[12.5px] text-white focus:outline-none focus:border-[#FF8533]/50 [color-scheme:dark]"
              />
              <span className="text-white/40 text-[12px]">to</span>
              <input
                type="date"
                value={customEnd}
                min={customStart || undefined}
                max={todayISO()}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="bg-white/[0.05] border border-white/10 rounded-full px-3 py-1.5 text-[12.5px] text-white focus:outline-none focus:border-[#FF8533]/50 [color-scheme:dark]"
              />
              <button
                type="button"
                onClick={fetchAll}
                className="bg-[#FF6E00] hover:bg-[#FF8533] text-white text-[12px] px-3.5 py-1.5 rounded-full transition-colors"
              >
                Apply
              </button>
            </div>
          )}
        </div>

        {/* Countries management + UTM generator */}
        <div className="grid lg:grid-cols-2 gap-4">
          <SectionCard title="Countries (Telegram Links)">
            <form onSubmit={saveCountry} className="grid grid-cols-2 gap-2 mb-4">
              <input
                type="text"
                required
                maxLength={3}
                value={countryForm.code}
                onChange={(e) => setCountryForm({ ...countryForm, code: e.target.value.toUpperCase() })}
                placeholder="Code (e.g. MY)"
                className="col-span-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-[13px] uppercase focus:outline-none focus:border-[#FF8533]/50"
              />
              <input
                type="text"
                required
                value={countryForm.name}
                onChange={(e) => setCountryForm({ ...countryForm, name: e.target.value })}
                placeholder="Name (Malaysia)"
                className="col-span-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-[13px] focus:outline-none focus:border-[#FF8533]/50"
              />
              <input
                type="text"
                value={countryForm.flag}
                onChange={(e) => setCountryForm({ ...countryForm, flag: e.target.value })}
                placeholder="Flag emoji (optional)"
                className="col-span-2 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-[13px] focus:outline-none focus:border-[#FF8533]/50"
              />
              <input
                type="url"
                required
                value={countryForm.telegram_link}
                onChange={(e) => setCountryForm({ ...countryForm, telegram_link: e.target.value })}
                placeholder="https://t.me/+xxxx"
                className="col-span-2 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-[13px] focus:outline-none focus:border-[#FF8533]/50"
              />
              <label className="col-span-1 flex items-center gap-2 text-white/70 text-[12.5px]">
                <input
                  type="checkbox"
                  checked={countryForm.enabled}
                  onChange={(e) => setCountryForm({ ...countryForm, enabled: e.target.checked })}
                  className="accent-[#FF6E00]"
                />
                Enabled
              </label>
              <button
                type="submit"
                disabled={savingCountry}
                className="col-span-1 inline-flex items-center justify-center gap-1.5 bg-[#FF6E00] hover:bg-[#FF8533] disabled:opacity-60 text-white text-[13px] py-2 rounded-full transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> {savingCountry ? 'Saving…' : 'Save / Update'}
              </button>
              {countryMsg && (
                <div className={`col-span-2 text-[12px] ${countryMsg.ok ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {countryMsg.text}
                </div>
              )}
            </form>

            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-[12.5px] min-w-[420px]">
                <thead>
                  <tr className="text-white/40 text-[10.5px] tracking-[0.12em] uppercase">
                    <th className="text-left px-2 py-1">Code</th>
                    <th className="text-left px-2 py-1">Name</th>
                    <th className="text-left px-2 py-1">Telegram</th>
                    <th className="text-center px-2 py-1">On</th>
                    <th className="text-right px-2 py-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {countries.length === 0 && (
                    <tr><td colSpan={5} className="text-white/40 text-center py-4">No countries</td></tr>
                  )}
                  {countries.map((c) => (
                    <tr key={c.code} className="border-t border-white/5">
                      <td className="px-2 py-2 text-white">{c.flag ? c.flag + ' ' : ''}{c.code}</td>
                      <td className="px-2 py-2 text-white/75">{c.name}</td>
                      <td className="px-2 py-2 text-white/60 truncate max-w-[180px]">{c.telegram_link}</td>
                      <td className="px-2 py-2 text-center">
                        <span className={c.enabled ? 'text-emerald-400' : 'text-white/30'}>{c.enabled ? '●' : '○'}</span>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => editCountry(c)}
                          className="text-[#FFA66B] hover:text-[#FF8533] text-[12px] mr-3"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteCountry(c.code)}
                          className="text-rose-400 hover:text-rose-300 inline-flex items-center"
                          aria-label="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>

          <SectionCard title="UTM Link Generator">
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-white/50 text-[10.5px] tracking-[0.12em] uppercase">Country</label>
                  <select
                    value={utmForm.country}
                    onChange={(e) => setUtmForm({ ...utmForm, country: e.target.value })}
                    className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-[13px] focus:outline-none focus:border-[#FF8533]/50"
                  >
                    {countries.filter((c) => c.enabled).map((c) => (
                      <option key={c.code} value={c.code} className="bg-black">
                        {c.flag} {c.code} — {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-white/50 text-[10.5px] tracking-[0.12em] uppercase">Campaign</label>
                  <input
                    type="text"
                    value={utmForm.campaign}
                    onChange={(e) => setUtmForm({ ...utmForm, campaign: e.target.value })}
                    placeholder="italy_launch_q4"
                    className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-[13px] focus:outline-none focus:border-[#FF8533]/50"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-[10.5px] tracking-[0.12em] uppercase">Source</label>
                  <input
                    type="text"
                    value={utmForm.source}
                    onChange={(e) => setUtmForm({ ...utmForm, source: e.target.value })}
                    placeholder="facebook"
                    className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-[13px] focus:outline-none focus:border-[#FF8533]/50"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-[10.5px] tracking-[0.12em] uppercase">Medium</label>
                  <input
                    type="text"
                    value={utmForm.medium}
                    onChange={(e) => setUtmForm({ ...utmForm, medium: e.target.value })}
                    placeholder="paid_social"
                    className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-[13px] focus:outline-none focus:border-[#FF8533]/50"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-white/50 text-[10.5px] tracking-[0.12em] uppercase">Ad Content / Variation (optional)</label>
                  <input
                    type="text"
                    value={utmForm.content}
                    onChange={(e) => setUtmForm({ ...utmForm, content: e.target.value })}
                    placeholder="video_hero_v1"
                    className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-[13px] focus:outline-none focus:border-[#FF8533]/50"
                  />
                </div>
              </div>

              <div>
                <label className="text-white/50 text-[10.5px] tracking-[0.12em] uppercase">Generated URL</label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    readOnly
                    value={utmUrl}
                    className="flex-1 bg-black/50 border border-[#FF8533]/25 rounded-xl px-3 py-2 text-white/90 text-[12px] font-mono focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={copyUtmUrl}
                    className="inline-flex items-center gap-1.5 bg-[#FF6E00] hover:bg-[#FF8533] text-white text-[12.5px] px-3 py-2 rounded-full transition-colors"
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p className="mt-2 text-white/40 text-[11.5px]">
                  Paste this URL into your Facebook / Instagram / TikTok ad&apos;s destination field. Country routing + UTM tracking runs automatically.
                </p>
              </div>
            </div>
          </SectionCard>
        </div>

        {/* Overview cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard
            icon={Users}
            label="Visitors"
            value={formatNum(summary?.visitors)}
            sub={`${formatNum(summary?.page_views)} page views`}
          />
          <StatCard
            icon={MousePointerClick}
            label="CTA Clicks"
            value={formatNum(summary?.clicks)}
          />
          <StatCard
            icon={Percent}
            label="Conversion"
            value={formatPct(summary?.conversion_rate)}
            sub="clicks / page views"
          />
          <StatCard
            icon={Globe}
            label="Top Country"
            value={summary?.top_country || '\u2014'}
            sub={
              summary?.top_country && COUNTRY_META[summary.top_country]
                ? COUNTRY_META[summary.top_country].name
                : ''
            }
          />
        </div>

        {/* Timeseries chart */}
        <SectionCard title="Traffic Over Time">
          <div className="h-64 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeseries}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    background: '#0b0b0b',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 8,
                    color: '#fff',
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: 'white' }} />
                <Line type="monotone" dataKey="visits" stroke="#FF6E00" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="clicks" stroke="#FFA66B" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        {/* Two-column: country table + source pie */}
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <SectionCard title="By Country">
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-[13px] min-w-[420px]">
                  <thead>
                    <tr className="text-white/40 text-[11px] tracking-[0.12em] uppercase">
                      <th className="text-left px-2 py-2">Country</th>
                      <th className="text-right px-2 py-2">Visits</th>
                      <th className="text-right px-2 py-2">Unique</th>
                      <th className="text-right px-2 py-2">Clicks</th>
                      <th className="text-right px-2 py-2">CVR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byCountry.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-white/40 text-center py-6">
                          No data yet
                        </td>
                      </tr>
                    )}
                    {byCountry.map((row) => {
                      const meta = COUNTRY_META[row.country];
                      return (
                        <tr key={row.country} className="border-t border-white/5">
                          <td className="px-2 py-3 text-white">
                            <span className="mr-1.5">{meta ? meta.flag : '\ud83c\udf10'}</span>
                            {meta ? meta.name : row.country || 'Unknown'}
                            <span className="ml-2 text-white/30 text-[11px]">{row.country}</span>
                          </td>
                          <td className="px-2 py-3 text-right text-white/85">{formatNum(row.visits)}</td>
                          <td className="px-2 py-3 text-right text-white/60">{formatNum(row.unique)}</td>
                          <td className="px-2 py-3 text-right text-white/85">{formatNum(row.clicks)}</td>
                          <td className="px-2 py-3 text-right text-[#FF8533]">{formatPct(row.cvr)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </div>

          <div className="space-y-4">
            <SectionCard title="Source Attribution">
              {sources.length === 0 ? (
                <div className="text-white/40 text-center py-6">No data</div>
              ) : (
                <div>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={sources}
                          dataKey="count"
                          nameKey="source"
                          innerRadius={35}
                          outerRadius={65}
                          paddingAngle={2}
                        >
                          {sources.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            background: '#0b0b0b',
                            border: '1px solid rgba(255,255,255,0.12)',
                            borderRadius: 8,
                            color: '#fff',
                            fontSize: 12,
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {sources.map((s, i) => (
                      <div key={s.source || i} className="flex items-center justify-between text-[12.5px]">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                          <span className="text-white/70 capitalize">{s.source || 'unknown'}</span>
                        </div>
                        <span className="text-white/85">
                          {formatNum(s.count)}{' '}
                          <span className="text-white/40">
                            ({totalSources ? Math.round((s.count / totalSources) * 100) : 0}%)
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </SectionCard>

            <SectionCard
              title="Device"
              right={<Smartphone className="w-4 h-4 text-white/40" />}
            >
              {devices.length === 0 ? (
                <div className="text-white/40 text-center py-4">No data</div>
              ) : (
                <div className="space-y-1.5">
                  {devices.map((d) => (
                    <div key={d.device} className="flex items-center justify-between text-[12.5px]">
                      <span className="text-white/70 capitalize">{d.device || 'unknown'}</span>
                      <span className="text-white/85">{formatNum(d.count)}</span>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        </div>

        {/* Referrers + UTM row */}
        <div className="grid lg:grid-cols-2 gap-4">
          <SectionCard title="Top Referrers" right={<LinkIcon className="w-4 h-4 text-white/40" />}>
            {referrers.length === 0 ? (
              <div className="text-white/40 text-center py-4">No data</div>
            ) : (
              <div className="space-y-1.5">
                {referrers.map((r) => (
                  <div key={r.referrer} className="flex items-center justify-between text-[12.5px]">
                    <span className="text-white/75 truncate max-w-[70%]">{r.referrer || 'direct'}</span>
                    <span className="text-white/85">{formatNum(r.count)}</span>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="UTM Campaigns">
            {utm.length === 0 ? (
              <div className="text-white/40 text-center py-4">
                No UTM data \u2014 add utm_source, utm_medium, utm_campaign to your ad URLs.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12px] min-w-[380px]">
                  <thead>
                    <tr className="text-white/40 text-[10.5px] tracking-[0.12em] uppercase">
                      <th className="text-left px-2 py-1">Campaign</th>
                      <th className="text-left px-2 py-1">Source</th>
                      <th className="text-right px-2 py-1">Visits</th>
                      <th className="text-right px-2 py-1">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {utm.map((u, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="px-2 py-2 text-white">{u.utm_campaign || '\u2014'}</td>
                        <td className="px-2 py-2 text-white/70">
                          {u.utm_source || '\u2014'} / {u.utm_medium || '\u2014'}
                        </td>
                        <td className="px-2 py-2 text-right">{formatNum(u.visits)}</td>
                        <td className="px-2 py-2 text-right">{formatNum(u.clicks)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </div>

        {/* Recent activity */}
        <SectionCard title="Recent Activity">
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-[12px] min-w-[560px]">
              <thead>
                <tr className="text-white/40 text-[10.5px] tracking-[0.12em] uppercase">
                  <th className="text-left px-2 py-1">Time (UTC)</th>
                  <th className="text-left px-2 py-1">Event</th>
                  <th className="text-left px-2 py-1">Country</th>
                  <th className="text-left px-2 py-1">Source</th>
                  <th className="text-left px-2 py-1">Device</th>
                  <th className="text-left px-2 py-1">Label / URL</th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-white/40 text-center py-4">
                      No events yet
                    </td>
                  </tr>
                )}
                {recent.map((r, i) => {
                  const meta = r.country ? COUNTRY_META[r.country] : null;
                  const t = r.timestamp ? r.timestamp.replace('T', ' ').slice(0, 19) : '';
                  return (
                    <tr key={r.id || i} className="border-t border-white/5">
                      <td className="px-2 py-2 text-white/60 whitespace-nowrap">{t}</td>
                      <td className="px-2 py-2">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${
                            r.event_type === 'cta_click'
                              ? 'bg-[#FF6E00]/15 text-[#FFA66B]'
                              : 'bg-white/10 text-white/70'
                          }`}
                        >
                          {r.event_type === 'cta_click' ? 'CTA' : 'View'}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-white/80">
                        {meta ? `${meta.flag} ${r.country}` : r.country || '\u2014'}
                      </td>
                      <td className="px-2 py-2 text-white/60">{r.country_source || '\u2014'}</td>
                      <td className="px-2 py-2 text-white/60 capitalize">{r.device || '\u2014'}</td>
                      <td className="px-2 py-2 text-white/75 truncate max-w-[280px]">
                        {r.label || r.cta_url || r.path || '\u2014'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>

        <div className="text-white/25 text-[11px] pt-2 pb-8">
          Auto-refresh every 30 seconds \u00b7 Ranges: 24h, 7d, 30d, 90d, all-time
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
